/**
 * festivalUtils.js
 * 
 * Festival date calculation and utilities for OxyLoans.
 * Accurately determines Diwali celebration dates across calendar years,
 * supporting the festive window (Dhanteras to Bhai Dooj) and test overrides.
 */

// Comprehensive Diwali date ranges across calendar years
// Format: { year: [ { start: [month, day], end: [month, day], exactDay: [month, day] } ] }
const DIWALI_CALENDAR = {
  2024: {
    start: [10, 29], // Oct 29 (Dhanteras)
    end: [11, 3],    // Nov 3 (Bhai Dooj)
    exactDay: [10, 31], // Oct 31 (Lakshmi Puja / Diwali)
  },
  2025: {
    start: [10, 18], // Oct 18 (Dhanteras)
    end: [10, 23],    // Oct 23 (Bhai Dooj)
    exactDay: [10, 20], // Oct 20 (Lakshmi Puja / Diwali)
  },
  2026: {
    // November 2026: Nov 4 through Nov 10 covers Diwali week & main Lakshmi Puja (Nov 8)
    start: [11, 4],  // Nov 4 (Diwali week start / Dhanteras prep)
    end: [11, 10],   // Nov 10 (Bhai Dooj)
    exactDay: [11, 8],  // Nov 8 (Lakshmi Puja / Diwali)
  },
  2027: {
    start: [10, 27],
    end: [11, 1],
    exactDay: [10, 29],
  },
  2028: {
    start: [10, 15],
    end: [10, 19],
    exactDay: [10, 17],
  },
  2029: {
    start: [11, 3],
    end: [11, 7],
    exactDay: [11, 5],
  },
  2030: {
    start: [10, 24],
    end: [10, 28],
    exactDay: [10, 26],
  },
};

/**
 * Checks if the current date falls on Diwali day / Diwali festive season.
 * 
 * Supports overrides for testing/previewing:
 * - URL query parameter: ?diwali=true / ?diwali=1 (or ?diwali=false / ?diwali=0)
 * - Session/Local storage: sessionStorage.setItem('forceDiwali', 'true')
 * 
 * @param {Date|null} customDate Optional specific date to test
 * @param {boolean} exactOnly If true, only matches the single exact Lakshmi Puja day
 * @returns {boolean} True if today is Diwali day / within Diwali festival
 */
export const isDiwaliDay = (customDate = null, exactOnly = false) => {
  // 1. Check URL query override (?diwali=true / ?diwali=false)
  if (typeof window !== "undefined") {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const queryParam = searchParams.get("diwali");
      if (queryParam === "true" || queryParam === "1") return true;
      if (queryParam === "false" || queryParam === "0") return false;

      // 2. Check storage override
      const stored =
        sessionStorage.getItem("forceDiwali") ||
        localStorage.getItem("forceDiwali");
      if (stored === "true" || stored === "1") return true;
      if (stored === "false" || stored === "0") return false;
    } catch {
      // Ignore URL/storage access errors in restricted sandbox
    }
  }

  // 3. Evaluate calendar date
  const now = customDate || new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // 1-12
  const day = now.getDate();

  const festivalInfo = DIWALI_CALENDAR[year];
  if (!festivalInfo) return false;

  const currentNumeric = month * 100 + day;

  if (exactOnly) {
    const [em, ed] = festivalInfo.exactDay;
    return currentNumeric === em * 100 + ed;
  }

  const [sm, sd] = festivalInfo.start;
  const [em, ed] = festivalInfo.end;
  const startNumeric = sm * 100 + sd;
  const endNumeric = em * 100 + ed;

  return currentNumeric >= startNumeric && currentNumeric <= endNumeric;
};

export default isDiwaliDay;
